import { flow, isNull, set } from 'lodash/fp';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useAppSelector } from '../../../hooks/react-redux';
import { Scrollable } from '../scrollable/Scrollable';
import { CalculationBlock } from './CalculationBlock';
import styles from './Calculator.module.scss';
import { CalculatorInputs } from './CalculatorInputs';
import { Calculation, CalculationBuilder as CalculationBuilderType, CalculationCondition, CalculationType, CalculatorField, CalculatorFieldTypeDB, CalculatorFieldTypeUI, CalculatorInput, getAllPadOptions, getCalculatorFieldType } from '../../admin/calculator/store';
import { newCalculatorField } from './constants';

const { lightGrey } = styles;

interface SharedCalculatorProps {
    value: CalculatorField[];
    showOutputField: boolean;
    showGracePeriod: boolean;
    includeFreeTextOption?: boolean;
    updateValue: (output: CalculatorField[]) => void;
    isEditing: boolean;
    testId?: string;
    disabled?: boolean;
    width: number;
    borderColor?: string;
    maxHeight?: number;
    minHeight?: string;
}

interface CalculatorProps extends SharedCalculatorProps {
    fieldType: string;
}

interface CalculatorWithKnownTypeProps extends SharedCalculatorProps {
    calculatorFieldType: CalculatorFieldTypeUI | CalculatorFieldTypeDB;
}

export const CalculatorWithKnownType: React.FC<CalculatorWithKnownTypeProps> = ({
    value,
    showOutputField,
    showGracePeriod,
    includeFreeTextOption = false,
    testId = '',
    disabled = false,
    isEditing = false,
    updateValue,
    width,
    borderColor = lightGrey,
    maxHeight = 426,
    minHeight = '30vh',
    calculatorFieldType
}) => {
    const [selectedBuilderIndex, setSelectedBuilderIndex] = useState<number | null>(0);
    const [selectedCalculationBuilder, setSelectedCalculationBuilder] = useState<CalculationType>(CalculationType.OUTPUT);
    const [selectedCalculationIndex, setSelectedCalculationIndex] = useState<number>(0);
    const bottomRef = useRef<HTMLDivElement>(null);
    const allPadOptions = useAppSelector(getAllPadOptions);

    const selectedCalculation = useMemo(() => value[selectedCalculationIndex], [value, selectedCalculationIndex]);

    const updateSelectedCalculation = useCallback((key: keyof CalculatorField, fieldValue: CalculationCondition | null | CalculationBuilderType | Calculation[]) => {
        updateValue(value.map((calc, index) => index === selectedCalculationIndex ? { ...selectedCalculation, [key]: fieldValue } : calc));
    }, [updateValue, value, selectedCalculationIndex, selectedCalculation]);

    const getCalculationConditionValue = useCallback((index: number, outputLength: number) => {
        if (index === outputLength - 1) {
            return outputLength === 1 ? null : CalculationCondition.ELSE;
        }
        return index === 0 ? CalculationCondition.IF : CalculationCondition.ELSE_IF;
    }, []);

    const removeCalculation = useCallback((blockIndex: number) => {
        // We have found that sometimes AI is populating an IF condition which cannot be removed on a single calculation array which it should not do
        if (value.length === 1) {
            const updatedOutput = value.map(calc => set('calculationCondition', null, calc));
            updateValue(updatedOutput);
            return;
        }
        setSelectedCalculationIndex(0);
        setSelectedBuilderIndex(0);
        const blockRemoved = value.filter((_, index) => index !== blockIndex);
        const updatedOutput = blockRemoved.map((calc, index) => {
            const calculationCondition = getCalculationConditionValue(index, blockRemoved.length);
            if (calculationCondition === CalculationCondition.ELSE) {
                return flow(
                    set('calculationCondition', calculationCondition),
                    set('calculation', [{ index: 0, builder: [] }])
                )(calc);
            }
            return set('calculationCondition', calculationCondition, calc);
        });
        updateValue(updatedOutput);
    }, [updateValue, value, setSelectedBuilderIndex, setSelectedCalculationIndex, getCalculationConditionValue]);

    const addNewCalculation = useCallback(() => {
        const existingUpdated = value.map((calc, index) => set('calculationCondition', index === 0 ? CalculationCondition.IF : CalculationCondition.ELSE_IF, calc));
        const newOutput = [...existingUpdated, newCalculatorField(CalculationCondition.ELSE)];
        if (bottomRef && bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        updateValue(newOutput);
    }, [updateValue, value]);

    const addOption = useCallback((value: CalculatorInput) => {
        if (isNull(selectedBuilderIndex)) {
            updateSelectedCalculation(selectedCalculationBuilder, [...selectedCalculation[selectedCalculationBuilder], value]);
        } else {
            updateSelectedCalculation('calculation', selectedCalculation.calculation.map(row => row.index === selectedBuilderIndex ? set('builder', [...row.builder, value], row) : row));
        }
    }, [updateSelectedCalculation, selectedCalculation, selectedBuilderIndex, selectedCalculationBuilder]);

    const addNewBuilder = useCallback(() => {
        const indexToInsert = isNull(selectedBuilderIndex) ? selectedCalculation.calculation.length : selectedBuilderIndex + 1;
        const updatedCalculation = [...selectedCalculation.calculation.slice(0, indexToInsert), { condition: null, builder: [], index: indexToInsert }, ...selectedCalculation.calculation.slice(indexToInsert)].map((calculation, index) => set('index', index, calculation));
        updateSelectedCalculation('calculation', updatedCalculation);
        setSelectedBuilderIndex(indexToInsert);
    }, [selectedCalculation, updateSelectedCalculation, selectedBuilderIndex]);

    const updateTextField = useCallback((blockIndex: number, calculatorProperty: 'calculation' | 'output' | 'gracePeriod', itemIndex: number, text: string, builderIndex?: number) => {
        if (['output', 'gracePeriod'].includes(calculatorProperty)) {
            const updatedBuilder = (value[blockIndex][calculatorProperty] as CalculationBuilderType).map((val, index) => index === itemIndex ? set('value', text, val) : val);
            const updatedValue = value.map((calc, index) => index === blockIndex ? set(calculatorProperty, updatedBuilder, value[blockIndex]) : calc);
            updateValue(updatedValue);
        } else {
            const updatedBuilder = value[blockIndex].calculation[builderIndex!].builder.map((val, index) => index === itemIndex ? set('value', text, val) : val);
            const updatedValue = value.map((calc, index) => index === blockIndex ? set(`calculation[${builderIndex}].builder`, updatedBuilder, calc) : calc);
            updateValue(updatedValue);
        }
    }, [updateValue, value]);

    const getFontSize = useCallback((optionInputType: string) => {
        const padOption = allPadOptions.find(({ inputType }) => inputType === optionInputType);
        if (padOption) {
            return padOption.config.fontSize;
        }
        return '14px';
    }, [allPadOptions]);

    const canRemoveCalculationBlock = useMemo(() => value.length > 1, [value.length]);

    const outputWrapperStyle = useMemo(() => isEditing && !disabled ? { width: 'calc(50% - 5px)', marginRight: '10px', maxHeight: `${maxHeight}px` } : { width: '100%', maxHeight: `${maxHeight}px` }, [isEditing, disabled, maxHeight]);
    return (
        <div className={styles.wrapper}>
            <div className={styles.calculatorWrapper} data-testid={`calculator-wrapper-${testId}`} style={{ minHeight }} >
                <div className={styles.outputRowsWrapper} style={outputWrapperStyle}>
                    <Scrollable>
                        {value.map(({ calculation, calculationCondition, output, gracePeriod }, calculationIndex) => (
                            <CalculationBlock
                                outputValue={value}
                                calculation={calculation}
                                output={output}
                                gracePeriod={gracePeriod}
                                calculationCondition={calculationCondition}
                                calculationIndex={calculationIndex}
                                getFontSize={getFontSize}
                                disabled={disabled}
                                isEditing={isEditing}
                                removeCalculation={removeCalculation}
                                selectedCalculation={selectedCalculation}
                                selectedCalculationIndex={selectedCalculationIndex}
                                selectedBuilderIndex={selectedBuilderIndex}
                                setSelectedBuilderIndex={setSelectedBuilderIndex}
                                setSelectedCalculationIndex={setSelectedCalculationIndex}
                                updateSelectedCalculation={updateSelectedCalculation}
                                updateTextField={updateTextField}
                                selectedCalculationBuilder={selectedCalculationBuilder}
                                setSelectedCalculationBuilder={setSelectedCalculationBuilder}
                                key={calculationIndex}
                                borderColor={borderColor}
                                showOutputField={showOutputField}
                                showGracePeriod={showGracePeriod}
                                canRemoveCalculationBlock={canRemoveCalculationBlock}
                            />
                        ))}
                    </Scrollable>
                </div>
                {isEditing && !disabled &&
                    <CalculatorInputs
                        calculatorFieldType={calculatorFieldType}
                        selectedCalculation={selectedCalculation}
                        selectedBuilderIndex={selectedBuilderIndex}
                        addOption={addOption}
                        addNewCalculation={addNewCalculation}
                        addNewBuilder={addNewBuilder}
                        disabled={disabled}
                        width={width}
                        selectedCalculationBuilder={selectedCalculationBuilder}
                        maxHeight={maxHeight}
                        showOutputField={showOutputField}
                        includeFreeTextOption={includeFreeTextOption}
                    />
                }
            </div>
            <div ref={bottomRef} />
        </div>
    );
};

export const Calculator: React.FC<CalculatorProps> = ({
    value,
    fieldType,
    showOutputField,
    showGracePeriod,
    includeFreeTextOption = false,
    testId = '',
    disabled = false,
    isEditing = false,
    updateValue,
    width,
    borderColor = lightGrey,
    maxHeight = 426,
    minHeight = '30vh'
}) => {
    const calculatorFieldType = useAppSelector(getCalculatorFieldType(fieldType));
    return (
        <CalculatorWithKnownType
            value={value}
            calculatorFieldType={calculatorFieldType}
            showOutputField={showOutputField}
            showGracePeriod={showGracePeriod}
            includeFreeTextOption={includeFreeTextOption}
            testId={testId}
            disabled={disabled}
            isEditing={isEditing}
            updateValue={updateValue}
            width={width}
            borderColor={borderColor}
            maxHeight={maxHeight}
            minHeight={minHeight}
        />
    );
};
